/**
 * Insights — Quality & Safety tab
 *
 * Quality KPIs come from /insights-module/quality (success rate proxy
 * from query_metrics + avg RAG sources). Trends come from
 * /insights-module/time-series.
 *
 * R3 P1 fix 2026-05-13 — wire Safety sub-tab to /insights-module/risk
 * (PR #430 backend) + Evaluation regression card to /evaluation/runs.
 * Faithfulness / satisfaction / citations remain empty (DeBERTa NLI
 * sidecar + feedback aggregation are still on the roadmap).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ShieldCheck, FileSearch, Target, ShieldAlert, FileCheck,
  Activity, Hash, MessageSquareWarning, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Legend,
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams, rangeLabel,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import {
  useInsightsQuality,
  useInsightsTimeSeries,
  useInsightsRisk,
  useEvaluationRuns,
} from '@/lib/use-insights';
import type { InsightsFilterParams } from '@/lib/use-insights';

export const Route = createFileRoute('/insights/quality')({
  component: QualitySafetyTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function QualitySafetyTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/quality');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);
  const [subTab, setSubTab] = useState<'quality' | 'safety'>('quality');

  // R3-P0 defect #1 — FilterBar filters propagate to backend.
  const filterParams = filtersToParams(filters);
  // Pod-KK #111 — labels follow the FilterBar range.
  const rl = rangeLabel(filters.range);

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6">
        <Tabs value={subTab} onValueChange={(v) => setSubTab(v as 'quality' | 'safety')}>
          <TabsList className="mb-5">
            <TabsTrigger value="quality" className="gap-1.5">
              <Target className="h-3.5 w-3.5" /> Quality
            </TabsTrigger>
            <TabsTrigger value="safety" className="gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5" /> Safety & Compliance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quality" className="space-y-5 mt-0">
            <QualitySection filterParams={filterParams} rl={rl} />
          </TabsContent>

          <TabsContent value="safety" className="space-y-5 mt-0">
            <SafetySection filterParams={filterParams} rl={rl} />
          </TabsContent>
        </Tabs>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}

// ─── Quality sub-tab ─────────────────────────────────────────────────────────

function QualitySection({ filterParams, rl }: { filterParams: InsightsFilterParams; rl: string }) {
  // #1021 — surface API failures so a stuck KPI never silently shows '—'.
  const {
    data: quality,
    isLoading: qualityLoading,
    isError: qualityError,
    error: qualityErrorObj,
  } = useInsightsQuality(filterParams);
  const { data: querySeries = [] } = useInsightsTimeSeries('queries', 30, filterParams);
  // R3 P1 — RAGAS faithfulness from /evaluation/runs.
  const { data: evalRuns = [] } = useEvaluationRuns();

  const successRate = quality ? quality.successRate / 100 : 0;
  const totalQueries = quality?.totalQueries30d ?? 0;
  const failedQueries = quality?.failedQueries30d ?? 0;
  const avgRag = quality?.avgRagSources ?? 0;

  // Average of the last 5 scored runs.
  const scoredRuns = evalRuns
    .filter((r) => r.avgFaithfulness !== null || r.avgRelevancy !== null)
    .slice(0, 5);
  const avgFaithfulness =
    scoredRuns.length > 0
      ? scoredRuns.reduce((sum, r) => sum + (r.avgFaithfulness ?? 0), 0) /
        scoredRuns.length
      : null;
  const avgRelevancy =
    scoredRuns.length > 0
      ? scoredRuns.reduce((sum, r) => sum + (r.avgRelevancy ?? 0), 0) /
        scoredRuns.length
      : null;

  const trendData = querySeries.map((p) => ({
    date: p.date,
    Queries: p.value,
  }));

  return (
    <>
      {/* #1021 — surface API failures explicitly. */}
      {qualityError && (
        <div
          role="alert"
          data-testid="insights-quality-summary-error"
          className="rounded-md border border-red-300/60 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-900 dark:text-red-200 mb-4"
        >
          Couldn't load quality summary
          {qualityErrorObj instanceof Error && qualityErrorObj.message
            ? ` — ${qualityErrorObj.message}`
            : '.'}
        </div>
      )}

      {/* KPIs — real from /insights-module/quality */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={`Success Rate (${rl})`}
          icon={ShieldCheck}
          value={qualityLoading ? '—' : `${(successRate * 100).toFixed(1)}%`}
          numericValue={successRate}
          subtitle="Non-error queries / total"
          statusDot={successRate >= 0.95 ? 'emerald' : successRate >= 0.85 ? 'amber' : 'red'}
        />
        <KpiCard
          label={`Total Queries (${rl})`}
          icon={Activity}
          value={qualityLoading ? '—' : totalQueries.toLocaleString()}
          numericValue={totalQueries}
          subtitle="Counted in query_metrics"
        />
        <KpiCard
          label={`Failed Queries (${rl})`}
          icon={AlertTriangle}
          value={qualityLoading ? '—' : failedQueries.toLocaleString()}
          numericValue={failedQueries}
          subtitle="status != 'success'"
          statusDot={failedQueries > totalQueries * 0.05 ? 'red' : 'emerald'}
        />
        <KpiCard
          label="Avg RAG Sources"
          icon={FileCheck}
          value={qualityLoading ? '—' : avgRag.toFixed(2)}
          numericValue={avgRag}
          subtitle={`Per query, last ${rl}`}
        />
      </div>

      {/* Real trend — query volume from /insights-module/time-series.
          Phrasing on empty-state placeholders intentionally matches the
          canonical honest-placeholder vocabulary asserted in
          tests/playwright/specs/smoke-insights.spec.ts (INSIGHTS-QUALITY-003)
          — one of: "not yet available", "not wired", "no <name> engine",
          "requires <name> job". Keep in sync with
          quality-honest-copy.test.ts. */}
      <ChartCard title={`Query volume (${rl})`} icon={Activity}>
        {trendData.length === 0 ? (
          <EmptySection
            message="Query-volume trend not yet available."
            hint="Trend populates from the query_metrics table once tenants run queries in the selected window."
            minHeight="min-h-48"
          />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) =>
                    new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis tick={{ fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{
                    fontSize: 11, borderRadius: 6,
                    border: '1px solid hsl(var(--border))',
                  }}
                />
                <Line type="monotone" dataKey="Queries" stroke="rgb(99 102 241)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      {/* R3 P1 — Faithfulness wired to /evaluation; satisfaction + citations
          still empty (need feedback aggregation + citation rollups) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Faithfulness & relevancy (RAGAS)" icon={ShieldCheck}>
          {avgFaithfulness === null ? (
            <EmptySection
              message="Faithfulness scores not yet available."
              hint="Requires a completed evaluation job from /evaluation; scores appear here as the LLM-as-judge pass completes."
              minHeight="min-h-48"
            />
          ) : (
            <div className="flex flex-col gap-4 px-2 py-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">
                    Avg faithfulness
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {Math.round(avgFaithfulness * 100)}%
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    over last {scoredRuns.length} run{scoredRuns.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">
                    Avg relevancy
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {avgRelevancy !== null
                      ? `${Math.round(avgRelevancy * 100)}%`
                      : '—'}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    LLM-as-judge
                  </div>
                </div>
              </div>
              <div className="text-[10px] italic text-muted-foreground">
                Satisfaction (thumbs-up rate) and citation coverage still need a
                feedback table + per-message citation rollups.
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Negative feedback themes" icon={MessageSquareWarning}>
          <EmptySection
            message="Feedback-aggregation pipeline not wired."
            hint="Requires a thumbs-down clustering job on the feedback table."
            minHeight="min-h-48"
          />
        </ChartCard>
      </div>

      {/* R3 P1 — wired to /evaluation/runs (RAGAS faithfulness + relevancy) */}
      <EvaluationRegressionCard />
    </>
  );
}

// ─── R3 P1 — Evaluation regression suite card ───────────────────────────────

function EvaluationRegressionCard() {
  const { data: runs = [], isLoading } = useEvaluationRuns();

  // Show the 5 most recent runs that have a faithfulness score.
  const scoredRuns = runs
    .filter((r) => r.avgFaithfulness !== null || r.avgRelevancy !== null)
    .slice(0, 5);

  if (isLoading) {
    return (
      <ChartCard title="Evaluation regression suite" icon={Target}>
        <EmptySection message="Loading evaluation runs…" minHeight="min-h-32" />
      </ChartCard>
    );
  }

  if (scoredRuns.length === 0) {
    return (
      <ChartCard title="Evaluation regression suite" icon={Target}>
        <EmptySection
          message="Evaluation regression results not yet available."
          hint="Requires a completed evaluation job from /evaluation; results appear here as soon as the LLM-as-judge pass completes."
        />
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Evaluation regression suite" icon={Target}>
      <div className="space-y-2">
        {scoredRuns.map((r) => {
          const faith = r.avgFaithfulness ?? 0;
          const rel = r.avgRelevancy ?? 0;
          const faithPct = Math.round(faith * 100);
          const relPct = Math.round(rel * 100);
          const minPct = Math.min(faithPct, relPct);
          const statusColor =
            minPct >= 95
              ? 'bg-emerald-500'
              : minPct >= 85
              ? 'bg-amber-500'
              : 'bg-red-500';
          const StatusIcon =
            minPct >= 95
              ? CheckCircle2
              : minPct >= 85
              ? AlertTriangle
              : ShieldAlert;

          return (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2"
            >
              <span className={`h-2 w-2 rounded-full ${statusColor}`} />
              <StatusIcon className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  Run {r.id.slice(0, 8)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.completedQuestions}/{r.totalQuestions} questions ·{' '}
                  {new Date(r.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              </div>
              <div className="flex gap-4 text-xs tabular-nums">
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Faithfulness
                  </div>
                  <div className="font-semibold">{faithPct}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Relevancy
                  </div>
                  <div className="font-semibold">{relPct}%</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}

// ─── Safety sub-tab — R3 P1 wired to /insights-module/risk ──────────────────

function SafetySection({ filterParams, rl: _rl }: { filterParams: InsightsFilterParams; rl: string }) {
  // R3-P0 defect #1 — propagate filters into every risk fetch. The 3
  // separate range windows are kept because the KPI tiles surface
  // different time slices ("Blocks (24h)", "Injection (7d)", ...).
  // Pod-KK #111 — Safety KPI cards already use explicit, accurate ranges
  // (24h / 7d / 30d) so they don't read from `rl`. The bottom-section
  // labels stay at 30d because they bind to `useInsightsRisk('30d', ...)`.
  const otherFilters = {
    department: filterParams.department,
    agentId: filterParams.agentId,
    modelId: filterParams.modelId,
  };
  const { data: risk24h } = useInsightsRisk('24h', otherFilters);
  const { data: risk7d } = useInsightsRisk('7d', otherFilters);
  const { data: risk30d } = useInsightsRisk('30d', otherFilters);

  // KPI #1 — Blocks (24h) — sum of audit_logs outcome IN ('blocked','denied')
  const blocks24h = risk24h?.blockedActions ?? 0;

  // KPI #2 — Injection (7d) — incident_type-keyed bucket
  const injection7d =
    risk7d?.byType.find((t) =>
      t.incidentType.toLowerCase().includes('injection'),
    )?.count ?? 0;

  // KPI #3 — Classification (7d) — classification + clearance buckets
  const classification7d =
    risk7d?.byType
      .filter((t) => {
        const k = t.incidentType.toLowerCase();
        return k.includes('classification') || k.includes('clearance');
      })
      .reduce((sum, t) => sum + t.count, 0) ?? 0;

  // KPI #4 — PII redactions (7d) — pii + redact buckets
  const pii7d =
    risk7d?.byType
      .filter((t) => {
        const k = t.incidentType.toLowerCase();
        return k.includes('pii') || k.includes('redact');
      })
      .reduce((sum, t) => sum + t.count, 0) ?? 0;

  // Severity-keyed colors for the by-severity bar
  const severityChartData = (risk30d?.bySeverity ?? []).map((s) => ({
    severity: s.severity,
    count: s.count,
  }));

  // Daily trend — incidents + blocked stacked over time
  const trendData = (risk30d?.trend ?? []).map((t) => ({
    date: t.date,
    Incidents: t.incidents,
    Blocked: t.blocked,
  }));

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Blocks (24h)"
          icon={ShieldAlert}
          value={blocks24h.toLocaleString()}
          numericValue={blocks24h}
          subtitle="Denied or blocked audit events"
          statusDot={blocks24h === 0 ? 'emerald' : blocks24h < 10 ? 'amber' : 'red'}
        />
        <KpiCard
          label="Injection (7d)"
          icon={ShieldAlert}
          value={injection7d.toLocaleString()}
          numericValue={injection7d}
          subtitle="Prompt-injection incidents"
          statusDot={injection7d === 0 ? 'emerald' : injection7d < 5 ? 'amber' : 'red'}
        />
        <KpiCard
          label="Classification (7d)"
          icon={ShieldAlert}
          value={classification7d.toLocaleString()}
          numericValue={classification7d}
          subtitle="Classification / clearance"
          statusDot={classification7d === 0 ? 'emerald' : 'amber'}
        />
        <KpiCard
          label="PII Redactions (7d)"
          icon={FileSearch}
          value={pii7d.toLocaleString()}
          numericValue={pii7d}
          subtitle="PII detections / redactions"
        />
      </div>

      {/* Bug #1117 — the Safety section binds to useInsightsRisk('30d', ...)
          (see line 408), so the "30d" labels + "in the last 30 days" copy
          are EXPLICITLY correct here regardless of the FilterBar range.
          Keep the labels at 30d so card titles match the data window. */}
      <ChartCard title="Incidents & blocked actions (30d)" icon={ShieldAlert}>
        {trendData.length === 0 ? (
          <EmptySection
            message="No incidents or blocked actions in the last 30 days."
            hint="Trend populates from governance_incidents + audit_logs as events accrue."
            minHeight="min-h-48"
          />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) =>
                    new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis tick={{ fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{
                    fontSize: 11, borderRadius: 6,
                    border: '1px solid hsl(var(--border))',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="Incidents" stackId="a" fill="rgb(168 85 247)" />
                <Bar dataKey="Blocked" stackId="a" fill="rgb(239 68 68)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Incidents by severity (30d)" icon={ShieldAlert}>
          {severityChartData.length === 0 ? (
            <EmptySection
              message="No incidents in the last 30 days."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={severityChartData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis dataKey="severity" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={40} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                  />
                  <Bar dataKey="count" fill="rgb(99 102 241)" name="Incidents" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Incidents by type (top 10)" icon={ShieldAlert}>
          {risk30d?.byType && risk30d.byType.length > 0 ? (
            <div className="space-y-1.5">
              {risk30d.byType.slice(0, 10).map((t) => (
                <div
                  key={t.incidentType}
                  className="flex items-center justify-between text-xs px-2 py-1.5 rounded border border-border"
                >
                  <span className="truncate">{t.incidentType}</span>
                  <span className="font-semibold tabular-nums">{t.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptySection
              message="No incident-type rollups in the last 30 days."
              minHeight="min-h-48"
            />
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Approvals funnel (30d)" icon={CheckCircle2}>
          <EmptySection
            message="Approvals funnel not surfaced in Insights."
            hint="Live approvals data is available under /approvals; needs an aggregation endpoint to drive this card."
          />
        </ChartCard>

        <ChartCard title="Audit chain integrity" icon={Hash}>
          <EmptySection
            message="Audit-chain status not surfaced in Insights."
            hint="Hash-chain verification runs in governance; expose its summary here when wired."
          />
        </ChartCard>
      </div>

      <ChartCard title="Policy rule hits (30d)" icon={FileCheck}>
        <EmptySection
          message="Per-rule hit counts not yet aggregated."
          hint="Policy decisions are logged but a rule-keyed rollup is missing."
        />
      </ChartCard>
    </>
  );
}
